import {
  Box,
  Checkbox,
  CheckboxGroup,
  Flex,
  Heading,
  InputGroup,
  Text,
} from '@chakra-ui/react'
import _ from 'lodash'
import { useContext, useEffect, useMemo, useState } from 'react'

import { useAppRoute } from 'routes/utils'

import { AuthContext } from 'contexts'

import { InlineLoading, PageHeader } from 'components'

import useReportCategories from 'api/cms/reports/useReportCategories'
import useReports, { useReportsByIds } from 'api/cms/reports/useReports'
import { Report, ReportCategory } from 'api/types'

import useTracking from 'tracking/useTracking'

import { apps, appsListUnion } from 'config/apps'

import useIsMobile from 'utils/useIsMobile'
import useThrottle from 'utils/useThrottle'

import { CategoriesDropdown } from './components/CategoriesDropdown'
import MainCard from './components/MainCard'
import MiniCard from './components/MiniCard'
import NothingFound from './components/NothingFound'
import { ReportSearchInput } from './components/ReportSearchInput'
import SlideModal from './components/SlideModal'
import { SortMenu } from './components/SortMenu'
import { groupByMonth } from './utils'
import { AccessGroups } from 'enums/AccessGroups'

const CheckboxSection = ({
  reportsApps,
  setReportsApps,
  activeReportApps,
}: {
  reportsApps: string[]
  setReportsApps: React.Dispatch<React.SetStateAction<string[]>>
  activeReportApps: string[]
}) => {
  if (!reportsApps) return null
  return (
    <Box w={'100%'} mt='8px' mb='12px'>
      <Text variant='body-bold' mb='4px' w='fit-content'>
        Filters{' '}
      </Text>
      <CheckboxGroup
        value={activeReportApps}
        defaultValue={reportsApps}
        onChange={(apps: string[]) => setReportsApps(apps)}
      >
        <Flex w={'100%'} flexWrap={'wrap'} gap={4}>
          {reportsApps.map((appValue) => (
            <Checkbox value={appValue} key={appValue}>
              <Text variant='body2' whiteSpace={'no-wrap'}>
                {appValue}
              </Text>
            </Checkbox>
          ))}
        </Flex>
      </CheckboxGroup>
    </Box>
  )
}

export interface ReportsPageRenderingProps {
  extraComponent?: React.ReactNode
  showCategory?: boolean
  showAppFilter?: boolean
  showSearchInput?: boolean
  title?: string
  slugOverwrites?: string[]
  category?: ReportCategory | null
  EmptyScreen?: React.ComponentType | null
  categories?: ReportCategory[]
  selectedCategory: ReportCategory | null
  setSelectedCategory: React.Dispatch<
    React.SetStateAction<ReportCategory | null>
  >
  searchString: string
  setSearch: (value: string) => void
  isLoading: boolean
  reportsApps: string[]
  setReportsApps: React.Dispatch<React.SetStateAction<string[]>>
  sortOrder: 'asc' | 'desc'
  setSortOrder: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>
  groupedReports: Record<string, Report[]>
  sortedReports: Report[]

  activeReportApps: string[]
}

const ReportsPageRendering: React.FC<ReportsPageRenderingProps> = ({
  extraComponent,
  showCategory = true,
  showAppFilter = false,
  showSearchInput = true,
  title = 'Reports',
  slugOverwrites = ['updates'],
  EmptyScreen,
  categories,
  selectedCategory,
  setSelectedCategory,
  searchString,
  setSearch,
  isLoading,
  reportsApps,
  setReportsApps,
  sortOrder,
  setSortOrder,
  groupedReports,
  sortedReports,
  activeReportApps,
}) => {
  const [selected, setSelected] = useState<Report | null>(null)
  const [isMobile] = useIsMobile()
  const [tracking] = useTracking()

  return (
    <Box overflow='auto' px={6} py={3}>
      <Box flex={1} pt={3} display='flex' flexDirection='column'>
        <PageHeader title={title} />
        {extraComponent}
        {showSearchInput && (
          <InputGroup flexDir={isMobile ? 'column' : 'row'}>
            {showCategory && categories && (
              <CategoriesDropdown
                isMobile={isMobile}
                categories={categories}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
            )}
            <ReportSearchInput
              isMobile={isMobile}
              onBlur={() =>
                searchString &&
                tracking.searchReports({ searchValue: searchString })
              }
              showCategory={showCategory}
              onChange={(e) => setSearch(e.target.value)}
            />
          </InputGroup>
        )}
        <SlideModal
          isMobile={isMobile}
          selected={selected}
          type={
            slugOverwrites ? `analyst-${slugOverwrites.join('-')}` : 'Report'
          }
          setSelected={() => setSelected(null)}
        />
        <Box border='1px solid' borderColor='gray3' borderRadius='8px' my={6}>
          {isLoading ? (
            <Flex alignItems='center' justifyContent='center' height='68px'>
              <InlineLoading />
            </Flex>
          ) : (
            <>
              <Flex
                justifyContent='space-between'
                minHeight='68px'
                px={8}
                alignItems='center'
              >
                {showAppFilter && (
                  <CheckboxSection
                    reportsApps={reportsApps}
                    setReportsApps={setReportsApps}
                    activeReportApps={activeReportApps}
                  />
                )}
                <Flex
                  alignItems={isMobile ? 'initial' : 'center'}
                  flexDir={isMobile ? 'column' : 'row'}
                  my={isMobile ? 4 : 0}
                  minH={isMobile ? 'max-content' : 'initial'}
                >
                  {(selectedCategory || searchString) && (
                    <Heading variant='h3' whiteSpace={'nowrap'} mr='1rem'>
                      {sortedReports.length} Result
                      {sortedReports.length > 1 && 's'}
                    </Heading>
                  )}
                  <SortMenu sortOrder={sortOrder} setSortOrder={setSortOrder} />
                </Flex>
              </Flex>
              {Object.keys(groupedReports).length > 0 ? (
                Object.keys(groupedReports).map((key, index) => {
                  return (
                    <MainCard key={index} keyStr={key}>
                      {groupedReports[key].map((report, i) => {
                        if (!report) return null
                        const { title } = report
                        return (
                          <MiniCard
                            isMobile={isMobile}
                            key={i}
                            onClick={() => {
                              tracking.openReport({
                                report: title,
                                type: 'summary',
                              })
                              setSelected(report)
                            }}
                            type={
                              slugOverwrites
                                ? `analyst-${slugOverwrites.join('-')}`
                                : 'Report'
                            }
                            report={report}
                          />
                        )
                      })}
                    </MainCard>
                  )
                })
              ) : EmptyScreen ? (
                <EmptyScreen />
              ) : (
                <NothingFound searchString={searchString} />
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  )
}

interface ReportPageByIdProps {
  reportIds: string[]
  extraComponent?: React.ReactNode
  showAppFilter?: boolean
  showSearchInput?: boolean
  title?: string
  slugOverwrites?: string[]
  EmptyScreen?: React.ComponentType | null
}

export const ReportPageById: React.FC<ReportPageByIdProps> = ({
  reportIds,
  extraComponent,
  showAppFilter = false,
  showSearchInput = true,
  title = 'Reports',
  slugOverwrites = ['updates'],
  EmptyScreen,
}) => {
  const [search, setSearch] = useState('')
  const searchString = useThrottle(search, 200)

  const [selectedReportApps, setReportsApps] = useState<string[]>([])
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const reportsRes = useReportsByIds('rsv-commercial', reportIds)
  const isLoading = reportsRes.isLoading

  const rawReports = useMemo(
    () => reportsRes.data as unknown as Report[],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading]
  )

  const searchedReports = useMemo(() => {
    if (!searchString) return rawReports
    const q = searchString.toLowerCase()
    return rawReports.filter((r) => (r.title || '').toLowerCase().includes(q))
  }, [rawReports, searchString])

  const sortedReports = useMemo(
    () =>
      _.orderBy(searchedReports, 'date', sortOrder).filter((report) =>
        report?.apps?.some((app: string) => selectedReportApps.includes(app))
      ),
    [searchedReports, sortOrder, selectedReportApps]
  )

  const groupedReports = useMemo(
    () => groupByMonth(sortedReports) as Record<string, Report[]>,
    [sortedReports]
  )

  const reportsApps = useMemo(
    () =>
      [...new Set(rawReports?.map((r) => r?.apps || []).flat())] as string[],
    [rawReports]
  )

  useEffect(() => {
    setReportsApps(reportsApps)
  }, [reportsApps])

  return (
    <ReportsPageRendering
      showCategory={false}
      categories={undefined}
      selectedCategory={null}
      setSelectedCategory={() => {}}
      extraComponent={extraComponent}
      showAppFilter={showAppFilter}
      showSearchInput={showSearchInput}
      title={title}
      slugOverwrites={slugOverwrites}
      EmptyScreen={EmptyScreen ?? null}
      searchString={searchString}
      setSearch={setSearch}
      isLoading={isLoading}
      reportsApps={reportsApps}
      setReportsApps={setReportsApps}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      groupedReports={groupedReports}
      sortedReports={sortedReports}
      activeReportApps={selectedReportApps}
    />
  )
}

type AppFilterRule = {
  label: string // What the UI shows
  include?: string[] // App names to include (union across selections)
  exclude?: string[] // App names to exclude (applied after includes)
}

type ReportPagePropTypes = {
  extraComponent?: React.ReactNode
  showCategory?: boolean
  showAppFilter?: boolean
  showSearchInput?: boolean
  title?: string
  slugOverwrites?: string[]
  category?: ReportCategory | null
  EmptyScreen?: React.FC | null
  customAppFilters?: Record<string, AppFilterRule>
  researchBrief?: boolean
}

const ReportPage: React.FC<ReportPagePropTypes> = ({
  extraComponent,
  showCategory = true,
  showAppFilter = false,
  showSearchInput = true,
  title = 'Reports',
  slugOverwrites = ['updates'],
  category,
  EmptyScreen,
  customAppFilters,
}) => {
  const { userInfo } = useContext(AuthContext)
  const selectedApp = useAppRoute()

  const [search, setSearch] = useState('')
  const searchString = useThrottle(search, 200)

  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [selectedCategory, setSelectedCategory] =
    useState<ReportCategory | null>(null)

  const appInfo = apps[selectedApp as appsListUnion]

  const hasPremiumAccess = useMemo(() => {
    const groups = appInfo?.premiumGroup ?? []
    return (
      groups.length > 0 &&
      !!userInfo?.groups?.some((g) => groups.includes(g as AccessGroups))
    )
  }, [appInfo?.premiumGroup, userInfo?.groups])

  const premiumSlug = useMemo(
    () => (hasPremiumAccess ? appInfo?.premiumSlug : undefined),
    [hasPremiumAccess, appInfo?.premiumSlug]
  )

  const categoriesRes = useReportCategories(
    selectedApp,
    slugOverwrites,
    { enabled: !!showCategory },
    premiumSlug
  )

  const categories = useMemo(
    () =>
      (categoriesRes ?? [])
        .flatMap((c) => (c?.data ?? []) as ReportCategory[])
        .filter(Boolean),
    [categoriesRes]
  )

  const effectiveCategory = selectedCategory || category || null

  const reportsRes = useReports(
    selectedApp,
    slugOverwrites,
    {
      categoryName: effectiveCategory?.name,
      categoryType: effectiveCategory?.type,
      searchText: searchString,
    },
    {},
    premiumSlug
  )

  const isLoading = useMemo(
    () => (reportsRes ?? []).some((res) => res?.isLoading),
    [reportsRes]
  )

  const rawReports: Report[] = useMemo(
    () =>
      (reportsRes ?? [])
        .flatMap((r) => (r?.data ?? []) as Report[])
        .filter(Boolean),
    [reportsRes]
  )

  const allReportsApps: string[] = useMemo(
    () => Array.from(new Set(rawReports.flatMap((r) => r?.apps ?? []))),
    [rawReports]
  )

  const [selectedReportApps, setSelectedReportApps] = useState<string[]>([])

  const reportsAppsForUI: string[] = useMemo(() => {
    if (!customAppFilters) return allReportsApps
    const entries = Object.values(customAppFilters).filter(
      Boolean
    ) as AppFilterRule[]
    return entries
      .sort(
        (a, b) =>
          Number(b.label === customAppFilters.all?.label) -
          Number(a.label === customAppFilters.all?.label)
      )
      .map((r) => r.label)
  }, [customAppFilters, allReportsApps])

  const labelToRule = useMemo(() => {
    if (!customAppFilters) return {} as Record<string, AppFilterRule>
    const map: Record<string, AppFilterRule> = {}
    Object.values(customAppFilters).forEach((r) => {
      if (r) map[r.label] = r
    })
    return map
  }, [customAppFilters])

  const activeReportApps: string[] = useMemo(() => {
    if (!customAppFilters || Object.keys(labelToRule).length === 0) {
      return selectedReportApps.length ? selectedReportApps : allReportsApps
    }

    if (!selectedReportApps.length) return allReportsApps

    let active = new Set<string>()
    let anyInclude = false

    for (const lbl of selectedReportApps) {
      const rule = labelToRule[lbl]
      if (!rule) continue
      if (rule.include?.length) {
        anyInclude = true
        for (const a of rule.include) {
          active.add(a)
        }
      }
    }

    if (!anyInclude) {
      active = new Set(allReportsApps)
    }

    for (const lbl of selectedReportApps) {
      const rule = labelToRule[lbl]
      rule?.exclude?.forEach((a) => active.delete(a))
    }

    const reportAppsKeys = Array.from(active).filter((a) =>
      allReportsApps.includes(a)
    )
    return reportAppsKeys
  }, [customAppFilters, labelToRule, selectedReportApps, allReportsApps])

  const sortedReports = useMemo(() => {
    const ordered = _.orderBy(rawReports, ['date'], [sortOrder])
    if (!activeReportApps.length) return ordered
    return ordered.filter((r) =>
      r.apps?.some((a) => activeReportApps.includes(a))
    )
  }, [rawReports, sortOrder, activeReportApps])

  const groupedReports = useMemo(
    () => groupByMonth(sortedReports) as Record<string, Report[]>,
    [sortedReports]
  )

  const _activeReportAppsLabels = customAppFilters
    ? !selectedReportApps.length
      ? reportsAppsForUI
      : selectedReportApps
    : activeReportApps

  return (
    <ReportsPageRendering
      extraComponent={extraComponent}
      showCategory={showCategory}
      showAppFilter={showAppFilter}
      showSearchInput={showSearchInput}
      title={title}
      slugOverwrites={slugOverwrites}
      category={category}
      EmptyScreen={EmptyScreen ?? null}
      categories={categories}
      selectedCategory={selectedCategory}
      setSelectedCategory={setSelectedCategory}
      searchString={searchString}
      setSearch={setSearch}
      isLoading={isLoading}
      reportsApps={reportsAppsForUI}
      activeReportApps={_activeReportAppsLabels}
      setReportsApps={setSelectedReportApps}
      sortOrder={sortOrder}
      setSortOrder={setSortOrder}
      groupedReports={groupedReports}
      sortedReports={sortedReports}
    />
  )
}

export default ReportPage
